/**
 * 📥 Import Results Modal Component
 * Displays import operation results, errors, and warnings
 */

import React from "react";
import SalaryRejectionsNotice from "./SalaryRejectionsNotice";
import { AlertTriangle, Lightbulb } from "lucide-react";
import ImportIssuesPanel from "./ImportIssuesPanel";
import { explainNotice } from "../../utils/importIssues";

export const ImportResultsModal = ({
  importResultsData,
  setImportResultsOpen,
  styles,
  fonts,
  theme,
  language,
  screenSize,
}) => {
  if (!importResultsData) return null;

  const imported = importResultsData.imported ?? 0;
  const updated = importResultsData.updated ?? 0;
  const skipped = importResultsData.skipped ?? 0;

  const allFailed = Array.isArray(importResultsData.failed) ? importResultsData.failed : [];
  const parseWarns = Array.isArray(importResultsData.parseWarnings) ? importResultsData.parseWarnings : [];
  const salaryRejections = Array.isArray(importResultsData.salaryRejections) ? importResultsData.salaryRejections : [];

  const totalFailed = new Set(allFailed.map((f) => f.row)).size;
  const notices = Array.isArray(importResultsData.notices) ? importResultsData.notices : [];
  const hasIssues = totalFailed > 0 || parseWarns.length > 0 || salaryRejections.length > 0 || notices.length > 0;

  // ✅ Inline helper components
  const pill = (label, value, color) => (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "4px",
      padding: "14px 20px",
      borderRadius: "14px",
      minWidth: "90px",
      background: theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      border: `1.5px solid ${color}30`,
    }}>
      <span style={{ fontSize: "1.75rem", fontWeight: 800, color, lineHeight: 1 }}>{value}</span>
      <span style={{ fontSize: fonts.tiny, fontWeight: 600, color: theme === "dark" ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
    </div>
  );

    return (
      <>
        {/* Backdrop */}
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 3000,
            background: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 300ms ease-out",
          }}
          onClick={() => setImportResultsOpen(false)}
        />

        {/* Modal Content */}
        <div style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 3001,
          animation: "slideUp 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          width: "min(860px, calc(100vw - 32px))",
          maxHeight: "88vh",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          margin: 0,
          background: theme === "dark"
            ? "linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)"
            : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          borderRadius: 16,
          padding: "32px",
          boxShadow: theme === "dark"
            ? "0 25px 60px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)"
            : "0 20px 50px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.04)",
          border: theme === "dark"
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(148,163,184,0.15)",
        }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ── */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
            <div>
              <h2 style={{
                margin: 0,
                marginBottom: "8px",
                fontSize: "24px",
                fontWeight: 800,
                background: hasIssues 
                  ? "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)"
                  : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                letterSpacing: "-0.02em",
              }}>
                {hasIssues ? "⚠️ " : "✅ "}
                {language === "ar" ? "نتيجة الاستيراد" : "Import Complete"}
              </h2>
              <p style={{ 
                margin: 0, 
                fontSize: fonts.small, 
                color: theme === "dark" ? "#94a3b8" : "#64748b",
                fontWeight: 500,
              }}>
                {language === "ar"
                  ? "تم معالجة ملف الإكسل. راجع التفاصيل أدناه."
                  : "The Excel file has been processed. Review the details below."}
              </p>
            </div>
            <button 
              onClick={() => setImportResultsOpen(false)}
              style={{
                position: "absolute",
                top: 20,
                right: 20,
                background: theme === "dark" 
                  ? "rgba(255,255,255,0.08)" 
                  : "rgba(0,0,0,0.05)",
                border: "none",
                borderRadius: "10px",
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: theme === "dark" ? "#94a3b8" : "#64748b",
                fontSize: "20px",
                fontWeight: 700,
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme === "dark" 
                  ? "rgba(255,255,255,0.12)" 
                  : "rgba(0,0,0,0.08)";
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = theme === "dark" 
                  ? "rgba(255,255,255,0.08)" 
                  : "rgba(0,0,0,0.05)";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              ✕
            </button>
          </div>

          {/* ── Summary pills ── */}
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {pill(language === "ar" ? "مُضافون" : "Created", imported, "#10b981")}
            {pill(language === "ar" ? "مُحدَّثون" : "Updated", updated, "#2e7cba")}
            {pill(language === "ar" ? "متخطَّيون" : "Skipped", skipped, "#f59e0b")}
            {pill(language === "ar" ? "فاشلة" : "Failed", totalFailed, "#ef4444")}
          </div>

          {/* ── No issues banner ── */}
          {!hasIssues && (
            <div style={{
              padding: "20px",
              borderRadius: "14px",
              background: theme === "dark" ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.06)",
              border: "1.5px solid rgba(16,185,129,0.3)",
              color: "#10b981",
              fontWeight: 600,
              fontSize: fonts.body,
              textAlign: "center",
            }}>
              {language === "ar"
                ? "🎉 تم استيراد جميع الصفوف بنجاح دون أي أخطاء!"
                : "🎉 All rows imported successfully with no issues!"}
            </div>
          )}

          {/* ── Salary changes rejected (existing employees) ── */}
          <SalaryRejectionsNotice
            rejections={salaryRejections}
            isRtl={language === "ar"}
            isDark={theme === "dark"}
            fonts={fonts}
            colors={{
              text: theme === "dark" ? "#e2e8f0" : "#1e293b",
              subtext: theme === "dark" ? "#94a3b8" : "#64748b",
              border: theme === "dark" ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.06)",
            }}
            committed
          />

          {/* ── Problems after the rows were saved (not tied to a row) ── */}
          {notices.map((notice) => {
            const n = explainNotice(notice, language === "ar");
            return (
              <div key={notice.code} style={{
                display: "flex", gap: 12, padding: "14px 16px", borderRadius: 14,
                background: theme === "dark" ? "rgba(245,158,11,0.08)" : "rgba(245,158,11,0.06)",
                border: "1px solid rgba(245,158,11,0.3)",
              }}>
                <AlertTriangle size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: fonts.small, fontWeight: 700, color: theme === "dark" ? "#f1f5f9" : "#0f172a" }}>{n.title}</span>
                  <span style={{ fontSize: fonts.tiny, color: theme === "dark" ? "#94a3b8" : "#64748b", lineHeight: 1.7 }}>{n.message}</span>
                  {n.fix && (
                    <span style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: fonts.tiny, color: theme === "dark" ? "#e2e8f0" : "#1e293b", lineHeight: 1.6 }}>
                      <Lightbulb size={13} color="#10b981" style={{ flexShrink: 0, marginTop: 3 }} />
                      {n.fix}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {/* ── Failed rows and warnings, explained per row ── */}
          <ImportIssuesPanel
            failed={allFailed}
            parseWarnings={parseWarns}
            isRtl={language === "ar"}
            isDark={theme === "dark"}
            fonts={fonts}
            committed
          />

          {/* ── Close button ── */}
          <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "12px", gap: "12px" }}>
            <button
              onClick={() => setImportResultsOpen(false)}
              style={{
                padding: "12px 32px",
                borderRadius: "10px",
                border: "none",
                background: "linear-gradient(135deg, #2e7cba 0%, #2e7cba 100%)",
                color: "#ffffff",
                fontSize: fonts.body,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                cursor: "pointer",
                //boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                transition: "all 0.2s",
                minWidth: "120px",
              }}
              // onMouseEnter={(e) => {
              //   e.currentTarget.style.transform = "translateY(-1px)";
              //   e.currentTarget.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
              // }}
              // onMouseLeave={(e) => {
              //   e.currentTarget.style.transform = "translateY(0)";
              //   e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
              // }}
            >
              {language === "ar" ? "إغلاق" : "Done"}
            </button>
          </div>
        </div>

        {/* Modal Animations */}
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translate(-50%, -50%) translateY(30px);
            }
            to {
              opacity: 1;
              transform: translate(-50%, -50%) translateY(0);
            }
          }
        `}</style>
      </>
    );
};

export default ImportResultsModal;
